import React, { Fragment, useState, useEffect } from 'react'
import { useBooking } from '../bookingContext'
import { Svg, validator } from '../../application'
import getText from '../strings'
import styled from 'react-emotion'
import UserConnected from './UserConnected'
import UserUninvited from './UserUninvited'
import UserChild from './UserChild'
import UserLeadPassenger from './UserLeadPassenger'
import { colors } from '../../styles'

const ManageUsersWrapper = styled('div')`
  border-bottom: 1px solid ${colors.tui_border};
  margin-bottom: 20px;
`

const Buttons = styled('div')`
  display: flex;
  justify-content: flex-end;
  @media (max-width: 992px) {
    .btn {
      width: 100%;
    } 
  }
`

const Disclaimer = styled('div')`
  display: flex;
  align-items: center;
  color: ${colors.tui_grey};;
  margin-bottom: 20px;
  svg {
    margin-right: 10px;
  }
`

const createInviteUpdates = (connectedUsers) => {
  return connectedUsers.map(p => {
    return {
      index: p.index,
      invitationExist: !!p.invitationExist,
      invitationEmail: p.invitationEmail || null,
      touched: false,
      valid: true
    }
  })
}

const ManageUsers = ({ onInviteUpdate, onRevokeInvite }) => {
  const bookingData = useBooking()
  const [loadingIndex, setLoadingIndex] = useState(-1)
  const [inviteUpdates, setInviteUpdates] = useState(createInviteUpdates(bookingData.connectedUsers))

  useEffect(() => {
    setInviteUpdates(createInviteUpdates(bookingData.connectedUsers))
    setLoadingIndex(-1)
  }, [bookingData.connectedUsers])

  const handleEmailChange = (index, email) => {
    setInviteUpdates(inviteUpdates.map(p =>
      p.index === index
        ? { ...p, invitationEmail: email, touched: true }
        : p
    ))
  }

  const handleRevokeInvite = (index) => {
    setInviteUpdates(inviteUpdates.map(p =>
      p.index === index
        ? { ...p, invitationExist: false, invitationEmail: null, touched: true }
        : p
    ))
    setLoadingIndex(index)
    onRevokeInvite(index)
  }

  const validateInvites = () => {
    setInviteUpdates(inviteUpdates.map(p => {
      if (!p.touched)
        return p

      const validationError = validator.email(p.invitationEmail)
      return {
        ...p,
        valid: !validationError,
        validationError
      }
    }))
  }

  const isSameEmail = (email, userIndex) => {
    return (email || '').toLowerCase() === (bookingData.connectedUsers.filter(x => x.index === userIndex)[0].email || '').toLowerCase()
  }

  const isSameInvitationExist = (invitationExist, userIndex) => {
    return !!invitationExist === !!bookingData.connectedUsers.filter(x => x.index === userIndex)[0].invitationExist
  }

  const isDirty = () => {
    return inviteUpdates
      .filter(p => p.touched)
      .filter(p => !(isSameEmail(p.invitationEmail, p.index) && isSameInvitationExist(p.invitationExist, p.index)))
      .length > 0
  }

  const isValid = () => {
    return inviteUpdates
      .filter(p => !p.valid)
      .length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (isDirty() && isValid()) {
      setLoadingIndex(0)
      onInviteUpdate(inviteUpdates.filter(i => i.invitationEmail))
    }
  }

  return (
    <Fragment>
      <ManageUsersWrapper>
        <h2>{getText('manage', 'travelingParty')}</h2>
        <p>{getText('manage', 'invite')}</p>
        {bookingData.connectedUsers.map((user, index) => {
          if (user.leadPassenger)
            return <UserLeadPassenger key={index} {...user} />
          if (user.type === "CHILD" || user.type === "INFANT")
            return <UserChild key={index} {...user} />
          const loading = loadingIndex === 0 || loadingIndex === user.index
          if (inviteUpdates.filter(p => p.index === user.index)[0].invitationExist) {
            return <UserConnected key={index} {...user} {...inviteUpdates.filter(p => p.index === user.index)[0]} onRevoke={handleRevokeInvite} loading={loading} />
          }
          return <UserUninvited key={index} {...user} {...inviteUpdates.filter(p => p.index === user.index)[0]} onChange={handleEmailChange} loading={loading} onBlur={() => validateInvites()} />
        })}
      </ManageUsersWrapper>
      <Disclaimer>
        <Svg icon="letter-info--circled" />
        {getText('manage', 'readOnly')}
      </Disclaimer>
      <Buttons>
        <button className='btn btn-function' disabled={loadingIndex !== -1 || !isDirty()} onClick={handleSubmit}>{getText('manage', 'inviteUser')}</button>
      </Buttons>
    </Fragment>
  )
}

export default ManageUsers